// THE RECEIPT (his "screen UI and confirmation msgs!!!!", 1 Nov 2026). After a booking the form used to clear itself and say
// what posted in one small line under an empty form — twice he read a booking that had worked as a form that had not. Now
// the form's card holds ONE receipt in the form's place: what was booked in plain words, its facts row by row (what POSTED,
// never the live fields), the reference to copy, and one button back to a fresh form. A deduped answer — the books already
// held this reference, a retry whose first attempt landed — says so in amber and shows NO facts: they would be the retry's,
// not the posting's, and could differ if the form was edited in between (Astra, 1 Nov 2026); Money movements shows the real
// entry. The page's own doors (Recent entries, Open party, Book the trade) sit under it.
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { CopyRef } from '../CopyRef';
import { displayAsset, fmtAmount, fmtTime } from '../../lib/format';
import { Fact, StateLine, TAP } from './kit';

/** A receipt's figure: mono tabular, the unit muted beside it, the two never split (Astra, 1 Nov 2026). */
export function ReceiptAmount({ value, asset }: { value: number | string | null | undefined; asset: string | null | undefined }) {
  return (
    <span className="whitespace-nowrap">
      <span className="font-mono tabular">{fmtAmount(value ?? null, asset ?? undefined)}</span>&nbsp;<span className="text-meta text-tg-hint">{displayAsset(asset ?? '')}</span>
    </span>
  );
}

/** The receipt's actions: the one primary, then each door on its own 44 px row on the phone, 8 px apart (their tap
 *  areas never overlap — Astra, 1 Nov 2026); one row from lg. */
function Actions({ another, links }: { another: { label: string; onClick: () => void }; links?: ReactNode }) {
  // The doors first, the one primary LAST — at the right from lg, as every form's foot puts it (1 Nov 2026 audit, P6); on
  // the phone the primary leads (column reversed), full width.
  return (
    <div className="flex flex-col-reverse gap-2 border-t border-stroke-soft pt-4 lg:flex-row lg:flex-wrap lg:items-center lg:justify-end lg:gap-x-5">
      {links && <div className="flex flex-col gap-2 lg:mr-auto lg:flex-row lg:items-center lg:gap-x-5 [&>*]:flex [&>*]:min-h-11 [&>*]:items-center lg:[&>*]:min-h-10">{links}</div>}
      <button type="button" className="btn-primary px-5 text-body max-lg:w-full" onClick={another.onClick}>{another.label}</button>
    </div>
  );
}

export interface ReceiptFact { label: string; value: ReactNode }

export function Receipt({ title, facts, reference, deduped = false, another, links, extra, movements = '/movements', movementsLabel = 'Money movements', already = 'Already recorded', tone = 'good' }: {
  /** What happened, in plain words: "Money in recorded", "Trade booked", "Converted". */
  title: string;
  /** 'warn': it booked, but something about it needs a look (no ✓, amber). */
  tone?: 'good' | 'warn';
  facts: ReadonlyArray<ReceiptFact | null | false | undefined>;
  /** The posting's reference, to copy. */
  reference?: string | null;
  /** The books already held this reference: nothing new was booked. */
  deduped?: boolean;
  /** The one way back to a fresh form: "Record another". */
  another: { label: string; onClick: () => void };
  /** The page's own doors after a booking ("Recent entries →", "Open party →"). */
  links?: ReactNode;
  /** A line the booking leaves for him (the Book-the-trade nudge). */
  extra?: ReactNode;
  /** Where a deduped answer's real entry is — Money movements, filtered as the form's own link is (a trade: Trades). */
  movements?: string;
  movementsLabel?: string;
  /** The deduped sentence's first words: "Already recorded", "Already booked". */
  already?: string;
}) {
  const rows = facts.filter(Boolean) as ReceiptFact[];
  if (deduped) {
    return (
      <div className="space-y-4" data-receipt="deduped">
        <StateLine tone="warn" action={<Link to={movements} className={TAP}>{movementsLabel} →</Link>}>
          {already}{reference ? <> as <span className="font-mono">{reference}</span></> : null} — nothing changed; check its date.
        </StateLine>
        <Actions another={another} />
      </div>
    );
  }
  return (
    <div className="space-y-4" data-receipt="">
      <h2 className={`flex items-center gap-2 text-title font-semibold ${tone === 'good' ? 'text-pos' : 'text-warn'}`}>
        {tone === 'good' && <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden />}
        {title}
      </h2>
      <div>
        {rows.map((r) => <Fact key={r.label} label={r.label}>{r.value}</Fact>)}
        {reference && (
          <Fact label="Reference">
            <span className="inline-flex items-center justify-end gap-2 font-mono text-meta"><CopyRef value={reference} /></span>
          </Fact>
        )}
      </div>
      {extra}
      <Actions another={another} links={links} />
    </div>
  );
}

/** The day for a back-dated entry; for one dated when it posted, the posting's own time — "12:30" (Dubai), never "now",
 *  which stops being true the moment the receipt is read (1 Nov 2026 audit, P6). `at` is the mutation's submittedAt. */
export function postedAsOf(asOf: string | null | undefined, day: (iso: string) => string, at?: number): string {
  if (asOf) return day(asOf);
  return at && at > 0 ? fmtTime(at) : 'now';
}
